import { forwardRef } from "react";
import Tooltip from "../../tooltip/Tooltip";
import "./_radio-group.scss";

const normalizeOption = (option) =>
  typeof option === "object" && option !== null ? option : { label: option, value: option };

/**
 * Pure-UI radio-button list. Mirrors `Checkbox`'s conventions (`value`/
 * `onChange` give/take the raw value, composed `Tooltip`, same disabled/
 * error treatment, hidden-native-input + decorative-box split) but renders
 * one native `<input type="radio">` per option instead of a single
 * checkbox - replaces `@simplifyhq/simplify-ui-reusables`'
 * `FormInputRadioGroup`. Accepts `title` as a `label` alias since
 * `renderInput.jsx`'s `radioButton` case passes `field.title || field.label`.
 */
const RadioGroup = forwardRef(function RadioGroup(
  {
    id,
    name,
    label,
    title,
    options = [],
    value,
    onChange,
    onBlur,
    error,
    disabled = false,
    tooltip,
    className = "",
  },
  ref
) {
  const groupLabel = label || title;
  const groupName = name || id;
  const normalized = options.map(normalizeOption);

  return (
    <Tooltip content={tooltip} disabled={!tooltip} className="radio-group-tooltip-anchor">
      <div
        ref={ref}
        className={`radio-group ${error ? "radio-group--error" : ""} ${
          disabled ? "radio-group--disabled" : ""
        } ${className}`}
      >
        {groupLabel && <span className="radio-group__label">{groupLabel}</span>}
        <div className="radio-group__options" role="radiogroup" aria-label={groupLabel}>
          {normalized.map((option) => (
            <label key={option.value} className="radio-group__row">
              <input
                type="radio"
                name={groupName}
                className="radio-group__input"
                checked={value === option.value}
                disabled={disabled}
                onChange={() => onChange?.(option.value)}
                onBlur={onBlur}
              />
              <span className="radio-group__dot" aria-hidden="true" />
              <span className="radio-group__option-label">{option.label}</span>
            </label>
          ))}
        </div>
        {error && <p className="radio-group__error">{error}</p>}
      </div>
    </Tooltip>
  );
});

export default RadioGroup;
